import logo from '@/assets/logohosp.png';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight, Heart, PawPrint, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useOnboardingContext } from '../context/OnboardingContext';
import { Button } from './ui/button';

interface OnboardingSlide {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  backgroundColor: string;
  textColor: string;
}

interface OnboardingPreLoginProps {
  onComplete: () => void;
  onSkip: () => void;
}

const slides: OnboardingSlide[] = [
  {
    id: 1,
    title: 'Bem-vindo ao SLZ Vet! 🐾',
    description: 'O Hospital Veterinário de São Luís agora está na palma da sua mão.',
    icon: <Heart size={64} className="text-white" />,
    backgroundColor: 'bg-gradient-to-br from-blue-500 to-blue-700',
    textColor: 'text-white'
  },
  {
    id: 2,
    title: 'Cadastre-se Gratuitamente',
    description: 'Crie sua conta rapidamente e tenha acesso a todos os serviços veterinários.',
    icon: <PawPrint size={64} className="text-white" />,
    backgroundColor: 'bg-gradient-to-br from-green-500 to-green-700',
    textColor: 'text-white'
  },
  {
    id: 3,
    title: 'Cuide dos seus Pets',
    description: 'Acesso completo a consultas, vacinas e acompanhamento veterinário profissional.',
    icon: <Heart size={64} className="text-white" />,
    backgroundColor: 'bg-gradient-to-br from-purple-500 to-purple-700',
    textColor: 'text-white'
  }
];

export function OnboardingPreLogin({ onComplete, onSkip }: OnboardingPreLoginProps) {
  const { setOnboardingActive } = useOnboardingContext();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: false,
    align: 'center',
    skipSnaps: false,
    dragFree: false
  });

  const scrollTo = useCallback((index: number) => {
    if (emblaApi) emblaApi.scrollTo(index);
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      if (currentSlide < slides.length - 1) {
        emblaApi.scrollNext();
      } else {
        onComplete();
      }
    }
  }, [emblaApi, currentSlide, onComplete]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCurrentSlide(emblaApi.selectedScrollSnap());
  }, [emblaApi, setCurrentSlide]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  // Marcar onboarding como ativo quando componente for montado
  useEffect(() => {
    setOnboardingActive(true);
    
    // Limpar quando componente for desmontado
    return () => {
      setOnboardingActive(false);
    };
  }, [setOnboardingActive]);

  const nextSlide = scrollNext;
  const previousSlide = scrollPrev;
  const goToSlide = scrollTo;

  return (
    <div className="fixed inset-0 z-[9999] bg-white animate-in fade-in duration-500">
      {/* Header com botão pular */}
      <div className="absolute top-4 right-4 z-10">
        <button
          onClick={onSkip}
          className="p-2 rounded-full bg-black/10 hover:bg-black/20 transition-all duration-200 hover:scale-110 active:scale-95"
          aria-label="Pular apresentação"
        >
          <X size={24} className="text-gray-700" />
        </button>
      </div>

      {/* Carousel Container */}
      <div className="embla h-full" ref={emblaRef}>
        <div className="embla__container h-full">
          {slides.map((slide) => (
            <div 
              key={slide.id} 
              className={`embla__slide min-w-full h-full flex flex-col justify-center items-center px-6 transition-all duration-700 ${slide.backgroundColor}`}
            >
              <div className="w-full max-w-md text-center">
                {/* Logo */}
                <div className="mb-6 flex justify-center animate-in slide-in-from-top duration-600 delay-100">
                  <img 
                    src={logo} 
                    alt="Hospital Veterinário SLZ" 
                    className="h-20 w-auto object-contain"
                  />
                </div>

                {/* Ícone com animação */}
                <div className="mb-8 flex justify-center">
                  <div className="p-4 rounded-full bg-white/20 transform transition-all hover:scale-110 animate-in zoom-in duration-700 delay-200">
                    {slide.icon}
                  </div>
                </div>

                {/* Título com animação */}
                <h1 className={`text-3xl font-bold mb-4 ${slide.textColor} animate-in slide-in-from-bottom duration-600 delay-300`}>
                  {slide.title}
                </h1>

                {/* Descrição com animação */}
                <p className={`text-lg mb-12 ${slide.textColor} opacity-90 animate-in slide-in-from-bottom duration-600 delay-500`}>
                  {slide.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Controles fixos */}
      <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 w-full max-w-md px-6">
        {/* Indicadores de progresso */}
        <div className="flex justify-center mb-8 space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 hover:scale-125 ${
                index === currentSlide
                  ? 'bg-white scale-125 shadow-lg'
                  : 'bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Ir para slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Navegação */}
        <div className="flex justify-between items-center w-full">
          <Button
            variant="outline"
            onClick={previousSlide}
            disabled={currentSlide === 0}
            className={`
              ${currentSlide === 0 ? 'invisible' : 'visible'}
              bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200 hover:scale-105 active:scale-95
            `}
          >
            <ChevronLeft size={20} className="mr-1" />
            Anterior
          </Button>

          <Button
            onClick={nextSlide}
            className="bg-white text-gray-900 hover:bg-gray-100 font-semibold px-8 transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl"
          >
            {currentSlide === slides.length - 1 ? (
              <span className="flex items-center">
                ✨ Vamos começar!
              </span>
            ) : (
              <>
                Próximo
                <ChevronRight size={20} className="ml-1" />
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Footer com informação adicional */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 animate-in slide-in-from-bottom duration-600 delay-1000">
        <p className="text-white/80 text-sm text-center">
          {currentSlide + 1} de {slides.length}
        </p>
      </div>
    </div>
  );
}